import { endpointDefinitionMap } from '../../../web/src/services/endpoints'
import type { ApiResult } from '../../../web/src/types'
import type { TmaAuthSession } from './telegramAuth'
import { resolveApiBaseUrl } from '../config/api-base-url'

const defaultApiBaseUrl = resolveApiBaseUrl()

const normalizeBaseUrl = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) {
    return 'http://localhost:4000'
  }

  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed
}

const buildRequestUrl = (baseUrl: string, path: string, query?: Record<string, string | undefined>): string => {
  const normalizedBase = normalizeBaseUrl(baseUrl)
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${normalizedBase}${normalizedPath}`)

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim()) {
        url.searchParams.set(key, value)
      }
    })
  }

  return url.toString()
}

const parseResponsePayload = async (response: Response): Promise<unknown> => {
  const responseText = await response.text()
  if (!responseText) {
    return undefined
  }

  try {
    return JSON.parse(responseText)
  } catch {
    return responseText
  }
}

export interface TmaApiClient {
  baseUrl: string
  authHeader?: string
  request(input: {
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE'
    path: string
    query?: Record<string, string | undefined>
    body?: unknown
  }): Promise<ApiResult>
  callEndpoint(endpointId: string, values?: Record<string, string>): Promise<ApiResult>
}

export const createTmaApiClient = (session: TmaAuthSession | null): TmaApiClient => {
  const authHeader = session ? `Bearer ${session.token}` : undefined

  return {
    baseUrl: defaultApiBaseUrl,
    authHeader,
    async request(input) {
      const timestamp = new Date().toISOString()
      const startedAt = performance.now()
      const url = buildRequestUrl(defaultApiBaseUrl, input.path, input.query)
      const requestPayload = input.body ?? (input.query ? { query: input.query } : undefined)

      try {
        const headers: Record<string, string> = {}

        if (authHeader) {
          headers.Authorization = authHeader
        }

        if (input.body) {
          headers['Content-Type'] = 'application/json'
        }

        const response = await fetch(url, {
          method: input.method,
          headers: Object.keys(headers).length ? headers : undefined,
          body: input.body ? JSON.stringify(input.body) : undefined,
        })

        const responsePayload = await parseResponsePayload(response)

        return {
          ok: response.ok,
          status: response.status,
          durationMs: Math.round(performance.now() - startedAt),
          timestamp,
          method: input.method,
          url,
          requestPayload,
          responsePayload,
          error: response.ok ? undefined : `HTTP ${response.status}`,
        }
      } catch (error) {
        return {
          ok: false,
          status: null,
          durationMs: Math.round(performance.now() - startedAt),
          timestamp,
          method: input.method,
          url,
          requestPayload,
          error: error instanceof Error ? error.message : String(error),
        }
      }
    },
    async callEndpoint(endpointId: string, values: Record<string, string> = {}) {
      const definition = endpointDefinitionMap[endpointId]
      if (!definition) {
        return {
          ok: false,
          status: null,
          durationMs: 0,
          timestamp: new Date().toISOString(),
          method: 'GET',
          url: endpointId,
          error: `Unknown endpointId: ${endpointId}`,
        }
      }

      const request = definition.buildRequest(values)
      return this.request({
        method: definition.method,
        path: request.path,
        query: request.query,
        body: request.body,
      })
    },
  }
}
